import React from "react";
import { FormState, ActivityFormProps } from "../types";
import {
  ALL_DIGITAL_VALUE,
  ALL_DIGITAL_LABEL,
  DIGITAL_TYPE,
} from "../utils/ValidationUtil";
import { StepHeader } from "../activity-components/FormComponents";

type SummaryEntry = [string, string | string[] | undefined];

type Props = {
  data: FormState;
  stepFocusRef: React.RefObject<HTMLDivElement>;
} & Pick<
  ActivityFormProps,
  | "agreePublishText"
  | "newsletterOptInText"
  | "startSelfCertifiedText"
  | "certificationText"
  | "wantToCertifyText"
  | "dontKnowText"
  | "descriptionOfTab1"
  | "descriptionOfTab5"
  | "startAcceptCriteriaText"
>;

// ── Hjälpkomponenter ──────────────────────────────────────────────────────────
function CheckItem({ html }: { html: string }) {
  return (
    <div className="g-col-12 activity__summary-item activity__summary-item--check">
      <span
        aria-hidden="true"
        className="activity__summary-item--check-icon"
        role="img"
      >
        ✔
      </span>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

function SummaryRow({
  label,
  value,
}: {
  label: string;
  value: string | string[];
}) {
  return (
    <div className="g-col-12 activity__summary-item">
      <strong>{label}:</strong>{" "}
      {Array.isArray(value) ? value.join(", ") : value}
    </div>
  );
}

function SummarySection({
  title,
  entries,
  modifier,
  children,
  description,
}: {
  title: string;
  entries?: SummaryEntry[];
  modifier?: string;
  children?: React.ReactNode;
  description?: string;
}) {
  const filtered = (entries ?? []).filter(([, v]) =>
    Array.isArray(v) ? v.length > 0 : !!v,
  );
  if (!filtered.length && !children) return null;

  return (
    <div className={`g-col-12 activity__summary-section ${modifier ?? ""}`}>
      <h3>{title}</h3>
      {description && (
        <p
          className="fst-italic ms-3"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      {children}
      {filtered.map(([k, v]) => (
        <SummaryRow key={k} label={k} value={v!} />
      ))}
    </div>
  );
}

export function Step5({
  data,
  stepFocusRef,
  descriptionOfTab1,
  descriptionOfTab5,
  agreePublishText,
  newsletterOptInText,
  startSelfCertifiedText,
  certificationText,
  wantToCertifyText,
  dontKnowText,
  startAcceptCriteriaText,
}: Props) {
  const isDigital =
    data.municipalities.includes(ALL_DIGITAL_VALUE) ||
    data.types.includes(DIGITAL_TYPE);

  const certHtmlMap: Record<string, string | undefined> = {
    accepted: certificationText,
    "want-to-certify": wantToCertifyText,
    "dont-know": dontKnowText,
  };

  const activityEntries: SummaryEntry[] = [
    ["Namn", data.name],
    ["Beskrivning", data.description],
    ["Typ av aktivitet", data.types],
    ["Ålder", data.ages],
    ["Kommun", isDigital ? [ALL_DIGITAL_LABEL] : data.municipalities],
    ["Prova-på", data.tryout],
    ["Med Fysisk aktivitet på recept, FaR", data.far],
    ["Plats", data.place],
    ["Adress", data.onlineOrNoSpecificAddress
      ? "Online eller ingen specifik adress"
      : data.addresses.map((location) => location.address)],
    ["Fritidskortet", data.fritidskortet ? "Ja" : undefined],
  ];

  const orgEntries: SummaryEntry[] = [
    ["Organisationsnummer", data.orgNumber],
    ["Telefonnummer", data.phone],
    ["E-post", data.email],
    ["Webbsida", data.website],
    ["Facebook", data.facebook],
    ["Instagram", data.instagram],
    ["TD-länk", data.tdLink],
  ];

  const contactEntries: SummaryEntry[] = [
    ["Kontakt-e-post", data.contactEmail],
    ["Kommentar till publicerare", data.contactComment],
  ];

  return (
    <>
      <StepHeader
        focusRef={stepFocusRef}
        ariaLabel="Steg 5: Sammanfattning"
        description={descriptionOfTab5}
        showRequiredInfo={false}
      />

      <div>
        {/* ── Start ── */}
        <div className="g-col-12 activity__summary-section">
          <h3>Start</h3>
          <div>
            {descriptionOfTab1 && (
              <div dangerouslySetInnerHTML={{ __html: descriptionOfTab1 }} />
            )}
            {data.startAcceptCriteria && startAcceptCriteriaText && (
              <CheckItem html={startAcceptCriteriaText} />
            )}

            <div
              className="my-3"
              style={{ borderBottom: "1px solid #DDDDDD" }}
            />
            {startSelfCertifiedText && <div> {startSelfCertifiedText} </div>}
          </div>

          {data.certificationStatus &&
            certHtmlMap[data.certificationStatus] && (
              <CheckItem html={certHtmlMap[data.certificationStatus]!} />
            )}
        </div>

        {/* ── Om aktiviteten ── */}
        <SummarySection title="Om aktiviteten" entries={activityEntries} />

        {/* ── Om oss ── */}
        <SummarySection
          title="Om oss"
          entries={orgEntries}
          modifier="activity__summary-section--org"
        />

        {/* ── Kontakt ── */}
        <SummarySection
          title="Kontakt"
          description="Dessa uppgifter används för kontakt med Trygg start-teamet och publiceras inte på Rörelseguiden."
          entries={contactEntries}
          modifier="activity__summary-section--contact"
        />

        <div className="activity__summary-section activity__summary-section--contact">
          {data.newsletterOptIn && newsletterOptInText && (
            <CheckItem html={newsletterOptInText} />
          )}
          {data.agreePublish && agreePublishText && (
            <CheckItem html={agreePublishText} />
          )}
        </div>
      </div>
    </>
  );
}
